// chapterPage/TasksPanel.jsx
// Coach-chat panel with in-progress issues + chat.
import React, { Component, createRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Icon, TypingIndicator } from '../../components/Icon';
import IssueReadOnlyCard from '../components/IssueReadOnlyCard';
import IssueFlowPanel from './IssueFlowPanel';
import { SeedMeter } from '../../components/SeedMeter';
import { NotificationContext } from '../../../state/notifications/NotificationContext';
import { getAuthHeaders, urlMap } from '../../../utils';

/**
 * TasksPanel — coach-chat panel for the current chapter.
 * Mirrors the ProjectCoachPanel approach: fetches master model, then ensures
 * a chapter-scoped session titled 'Tasks'.
 *
 * Props:
 *  - chapter       : object
 *  - projectId     : string
 *  - projectIssues : array
 *  - onClose       : () => void
 *  - onChapterRefresh? : () => void
 */
export default class TasksPanel extends Component {
  static contextType = NotificationContext;
  textareaRef = createRef();
  readerAbortController = null;

  state = {
    issueMessages: [],
    reflectionMessages: [],
    error: null,
    sending: false,
    typing: false,
    input: '',
    activeTab: 'issue', // 'issue' | 'reflection'
    projectIssues: Array.isArray(this.props.projectIssues) ? [...this.props.projectIssues] : [],
    flowIssue: null,
    helpSessionId: null,
    reflectionSessionId: null,
    freeTextSessionId: null,
    freeTextModelId: null,
    selectedIssueId: null,
    seedsUsed: 0,
    reflectionTriggered: false,
    currentReflectionId: null, // Track active reflection for storing responses
    isReflectionPhase: false, // Flag to know if we're in reflection mode
    currentReflectionQuestion: null, // Current question being asked
    reflectionQuestionIndex: 0, // Track which question we're on (0-3)
    reflectionIsComplete: false, // Track if all questions answered
    reflectionRecordCreated: false, // Track if reflection record has been created in DB
    reflectionWriterSessionId: null, // Writer session linked to this reflection run
    enhancedReflection: true,
  };

  componentDidMount() {
    // If the panel was opened via the timer's "Reflect" button, start reflection.
    if (this.props.reflectSignal) {
      this.startReflectionFromSignal();
    }
  }

  componentDidUpdate(prev) {
    if (prev.projectIssues !== this.props.projectIssues) {
      const nextIssues = Array.isArray(this.props.projectIssues) ? [...this.props.projectIssues] : [];
      this.setState((s) => {
        const stillExists = nextIssues.some((i) => i.id === s.selectedIssueId);
        return {
          projectIssues: nextIssues,
          selectedIssueId: stillExists ? s.selectedIssueId : null,
        };
      });
    }

    // Manual "Reflect" button press from the Pomodoro timer.
    if (this.props.reflectSignal && this.props.reflectSignal !== prev.reflectSignal) {
      this.startReflectionFromSignal();
    }

    // Trigger reflection exercise when timer transitions from work to reflection phase
    const prevPhase = prev.writerModeTimerPhase;
    const currPhase = this.props.writerModeTimerPhase;
    if (prevPhase === 'work' && currPhase === 'reflection' && !this.state.reflectionTriggered) {
      this.activateReflectionTab();
      this.setState({ reflectionTriggered: true });
    }

    // Reset reflection trigger when a new writing cycle starts.
    if ((prevPhase === 'reflection' || prevPhase === 'rest') && currPhase === 'work') {
      this.setState({ reflectionTriggered: false });
    }
  }

  // Start a reflection session in response to the manual Reflect button, then
  // clear the signal so reopening the panel doesn't retrigger it.
  startReflectionFromSignal = () => {
    this.activateReflectionTab();
    this.props.onReflectionConsumed?.();
  };

  componentWillUnmount() {
    try { this.readerAbortController?.abort(); } catch {}
  }

  // ── reset ──

  onReset = async () => {
    if (this.state.activeTab === 'reflection') {
      this.setState({
        input: '',
        reflectionMessages: [],
        reflectionSessionId: null,
        currentReflectionQuestion: null,
        reflectionQuestionIndex: 0,
        isReflectionPhase: false,
        reflectionIsComplete: false,
        error: null,
      });
      await this.triggerPomodoroReflection();
      this.focusTextarea();
      return;
    }

    this.setState({ issueMessages: [], reflectionMessages: [], helpSessionId: null, reflectionSessionId: null, error: null });
  };

  // ── input ──

  onChange = (e) => {
    this.setState({ input: e.target.value }, () => {
      const el = this.textareaRef.current;
      if (el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
      }
    });
  };

  onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      this.handleSend();
    }
  };

  // ── send / stream ──

  getOrFetchFreeTextModelId = async () => {
    if (this.state.freeTextModelId) return this.state.freeTextModelId;

    const res = await fetch(urlMap.coachModel(), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data = await res.json();
    const modelId = data?.model?.id;
    if (!modelId) throw new Error('NO_COACH_MODEL');

    this.setState({ freeTextModelId: modelId });
    return modelId;
  };

  ensureFreeTextSession = async (chapterId) => {
    if (this.state.freeTextSessionId) {
      return { sessionId: this.state.freeTextSessionId, llmId: this.state.freeTextModelId };
    }

    const llmId = await this.getOrFetchFreeTextModelId();
    const createRes = await fetch(urlMap.createChatSession, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({
        llmId,
        chapterId,
        title: 'Tasks',
      }),
    });

    if (!createRes.ok) throw new Error(`HTTP ${createRes.status}`);
    const created = await createRes.json();
    if (!created?.id) throw new Error('FREE_TEXT_SESSION_CREATE_FAILED');

    this.setState({ freeTextSessionId: created.id, freeTextModelId: llmId });
    return { sessionId: created.id, llmId };
  };

  toggleIssueSelection = (issueId) => {
    this.setState((s) => ({
      selectedIssueId: s.selectedIssueId === issueId ? null : issueId,
    }));
  };

  parseReflectionContent = (text) => {
    // Extract reflection answers from user message
    // Format: answers to 4 questions separated by natural text
    const lines = text.split('\n').filter(l => l.trim());
    
    let accomplished = null;
    let blocker = null;
    let nextAction = null;
    let difficultyScore = null;

    // Simple parsing: look for difficulty score first (easier to extract)
    const difficultyMatch = text.match(/(\d)\s*[-–]\s*5/i) || 
                           text.match(/difficulty.*?(\d)/i) ||
                           text.match(/hard|difficult|easy|challenging/) &&
                           text.match(/(\d)\s*[/-]?\s*5/) ||
                           text.match(/(\d)(?:\s+out\s+of\s+5)?/);
    
    if (difficultyMatch) {
      const score = parseInt(difficultyMatch[1], 10);
      if (score >= 1 && score <= 5) {
        difficultyScore = score;
      }
    }

    // Try to extract the four answers (this is heuristic)
    const fullText = text.toLowerCase();
    
    // Accomplished (first substantive answer)
    if (lines.length > 0) {
      // Look for non-trivial lines
      const substantiveLines = lines.filter(l => l.length > 10);
      if (substantiveLines.length > 0) {
        accomplished = substantiveLines[0];
      }
    }

    // Blocker/Difficulty (mentions of "blocked", "stuck", "nothing", "none")
    const blockerMatch = text.match(/(?:nothing|none|no|blocked|stuck|struggling).*?[\.\?!]/i);
    if (blockerMatch) {
      blocker = blockerMatch[0].slice(0, 200); // Keep under 200 chars
    }

    // Next action (last substantive thought)
    if (lines.length > 1) {
      const substantiveLines = lines.filter(l => l.length > 10);
      if (substantiveLines.length > 1) {
        nextAction = substantiveLines[substantiveLines.length - 1];
      }
    }

    return { accomplished, blocker, nextAction, difficultyScore };
  };

  updateReflectionRecord = async (reflectionContent) => {
    const { writerSessionId } = this.props;
    const { currentReflectionId } = this.state;

    if (!currentReflectionId || !writerSessionId) return;

    try {
      await fetch(
        urlMap.updateWriterSessionReflection(writerSessionId, currentReflectionId),
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            accomplished: reflectionContent.accomplished,
            blocker: reflectionContent.blocker,
            nextAction: reflectionContent.nextAction,
            difficultyScore: reflectionContent.difficultyScore,
          }),
        }
      );
    } catch (err) {
      console.warn('Failed to update reflection record:', err);
    }
  };

  focusTextarea = () => {
    const el = this.textareaRef.current;
    if (el) el.focus();
  };

  isReflectionRestartGreeting = (text) => {
    const normalized = String(text || '').trim().toLowerCase();
    return /^(hi|hello|hey)[!.?]*$/.test(normalized);
  };

  handleSend = async () => {
    const { helpSessionId, input, isReflectionPhase, activeTab } = this.state;
    const text = input.trim();
    if (!text) return;

    const chapterId = this.props.chapter?.id;
    if (!chapterId) return;

    // Reflection tab always routes through the reflection endpoint flow.
    if (activeTab === 'reflection') {
      if (!isReflectionPhase) {
        if (!this.isReflectionRestartGreeting(text)) return;

        this.setState({ input: '', reflectionMessages: [], error: null });
        await this.triggerPomodoroReflection();
        this.setState({ sending: false, typing: false }, this.focusTextarea);
        return;
      }
      this.setState((s) => ({
        input: '',
        reflectionMessages: [...s.reflectionMessages, { id: `local-${Date.now()}`, from: 'me', text }],
      }));

      // Apply response and get next question
      await this.applyReflectionResponseAndGetNext(text);
      this.setState({ sending: false, typing: false }, this.focusTextarea);
      return;
    }

    const selectedIssueId = this.getAskIssueId();

    this.setState((s) => ({
      input: '',
      sending: true,
      typing: true,
      issueMessages: [...s.issueMessages, { id: `local-${Date.now()}`, from: 'me', text }],
    }));

    try {
      if (selectedIssueId) {
        const body = { chapterId, issueId: selectedIssueId, message: text };
        if (helpSessionId) body.sessionId = helpSessionId;

        const res = await fetch(urlMap.createIssueChatMessage(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify(body),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const run = await res.json();

        const activeSessionId = helpSessionId || run?.sessionId;
        if (activeSessionId && !helpSessionId) {
          this.setState({ helpSessionId: activeSessionId });
        }

        if (run?.runId && activeSessionId) {
          await this.streamAssistantSSE(activeSessionId, run.runId, true);
        } else if (run?.runId && !activeSessionId) {
          throw new Error('No issue-solver session available');
        } else {
          this.setState({ sending: false, typing: false }, this.focusTextarea);
        }
      } else {
        const { sessionId, llmId } = await this.ensureFreeTextSession(chapterId);

        const res = await fetch(urlMap.createChatMessage(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
          body: JSON.stringify({
            sessionId,
            llmId,
            content: [{ type: 'text', text }],
          }),
        });

        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const run = await res.json();

        if (run?.runId) {
          await this.streamAssistantSSE(sessionId, run.runId, false);
        } else {
          this.setState({ sending: false, typing: false }, this.focusTextarea);
        }
      }
    } catch (err) {
      this.setState({ sending: false, typing: false, error: err.message || 'Failed to send' }, this.focusTextarea);
    }
  };

  async streamAssistantSSE(sessionId, runId, useIssueStream = false) {
    const streamUrl = new URL(
      useIssueStream ? urlMap.streamIssueMessage(runId) : urlMap.streamRun(runId),
      window.location.origin
    );
    streamUrl.searchParams.set('sessionId', sessionId);

    const draftId = `assist-${runId}`;
    this.setState((s) => ({
      issueMessages: [...s.issueMessages, { id: draftId, from: 'them', text: '' }],
    }));

    this.readerAbortController = new AbortController();

    try {
      const res = await fetch(streamUrl.toString(), {
        method: 'GET',
        headers: { ...getAuthHeaders(), Accept: 'text/event-stream' },
        signal: this.readerAbortController.signal,
      });

      if (!res.ok || !res.body) {
        this.updateBubble(draftId, '⚠️ Stream failed.');
        this.setState({ sending: false }, this.focusTextarea);
        return;
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assembled = '';
      let firstChunk = true;

      const feed = (chunk) => {
        buffer += chunk;
        const frames = buffer.split('\n\n');
        buffer = frames.pop() || '';

        for (const frame of frames) {
          for (const line of frame.split('\n')) {
            const t = line.trim();
            if (!t.startsWith('data:')) continue;
            try {
              const evt = JSON.parse(t.slice(5).trim());
              const kind = evt.event ?? evt.type;
              const delta = evt.delta ?? evt.payload?.delta;
              if (kind === 'delta' && delta?.type === 'text' && typeof delta.text === 'string') {
                assembled += delta.text;
                this.updateBubble(draftId, assembled);
              } else if (kind === 'done') return true;
            } catch {}
          }
        }
        return false;
      };

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        if (firstChunk && chunk) {
          this.setState({ typing: false });
          firstChunk = false;
        }
        if (feed(chunk)) break;
      }
      if (buffer) feed(decoder.decode());

      this.updateBubble(draftId, assembled || '(no response)');
      this.setState((s) => ({ typing: false, seedsUsed: s.seedsUsed + 1 }));
      this.context?.notifyWalletRefresh?.();
    } catch (err) {
      if (err?.name !== 'AbortError') this.updateBubble(draftId, '⚠️ Connection lost.');
      this.setState({ typing: false });
    } finally {
      this.setState({ sending: false }, this.focusTextarea);
      this.readerAbortController = null;
    }
  }

  updateBubble(id, text) {
    this.setState(
      (s) => ({ issueMessages: s.issueMessages.map((m) => (m.id === id ? { ...m, text } : m)) }),
      () => {
        const el = document.querySelector('.coach-messages');
        if (el) el.scrollTop = el.scrollHeight;
      }
    );
  }

  handleFlowHelp = async ({ issue, step }) => {
    const issueId = issue?.id;
    if (!issueId) return;

    const stepText = step?.title || step?.prompt || step?.front || step?.description || '';
    const stepKind = step?.type ? String(step.type).replace(/_/g, ' ') : 'step';
    const scaffoldingText = step?.scaffolding_level
      ? `Current support level: ${step.scaffolding_level}`
      : null;
    const text = [
      `I need help solving this issue: ${issue.title || '(untitled)'}.`,
      stepText ? `Current ${stepKind}: ${stepText}` : null,
      scaffoldingText,
      'Reply with one concrete edit I should make now in this chapter.',
      'Plain text only. No headings, no bullets, no markdown, no quoted excerpts.',
    ].filter(Boolean).join('\n');

    const { chapter } = this.props;
    const chapterId = chapter?.id;
    if (!chapterId) return;

    this.setState((s) => ({
      sending: true,
      typing: true,
      issueMessages: [...s.issueMessages, { id: `local-${Date.now()}`, from: 'me', text }],
    }));
  
    try {
      const { helpSessionId } = this.state;
      const body = { chapterId, issueId, message: text };
      if (helpSessionId) body.sessionId = helpSessionId;

      const res = await fetch(urlMap.createIssueChatMessage(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const run = await res.json();

      const activeSessionId = helpSessionId || run?.sessionId;
      if (activeSessionId && !helpSessionId) {
        this.setState({ helpSessionId: activeSessionId });
      }

      if (run?.runId && activeSessionId) {
        await this.streamAssistantSSE(activeSessionId, run.runId);
      } else if (run?.runId && !activeSessionId) {
        throw new Error('No issue-solver session available');
      } else {
        this.setState({ sending: false, typing: false });
      }
    } catch (err) {
      this.setState({ sending: false, typing: false, error: err.message || 'Failed to send' });
    }
  };

  // ── helpers ──

  getInProgressIssues() {
    const severityOrder = { critical: 0, high: 1, medium: 2, low: 3 };
    const chapterId = this.props.chapter?.id;
    return (this.state.projectIssues || [])
      .filter((i) => i.status === 'in_progress')
      .filter((i) => !chapterId || i.chapter_id === chapterId)
      .sort((a, b) => (severityOrder[a.severity] ?? 9) - (severityOrder[b.severity] ?? 9));
  }

  // Ask chat should include issue context ONLY when explicitly selected.
  getAskIssueId() {
    return this.state.selectedIssueId || null;
  }

  // Reflection chat should always carry an issue id for context.
  // If nothing is selected, fall back to the top in-progress issue.
  getReflectionIssueId() {
    return this.state.selectedIssueId || this.getInProgressIssues()[0]?.id || null;
  }

  skipToReflection = () => {
    this.activateReflectionTab();
  };

  toggleEnhancedReflection = () => {
    this.setState(
      (s) => ({ enhancedReflection: !s.enhancedReflection }),
      async () => {
        // If already in an active reflection flow, restart so mode applies immediately.
        if (this.state.activeTab === 'reflection' && (this.state.isReflectionPhase || this.state.reflectionSessionId)) {
          this.setState({
            input: '',
            reflectionMessages: [],
            reflectionSessionId: null,
            currentReflectionQuestion: null,
            reflectionQuestionIndex: 0,
            isReflectionPhase: false,
            reflectionIsComplete: false,
            error: null,
          });
          await this.triggerPomodoroReflection();
        }
      },
    );
  };

  activateReflectionTab = () => {
    this.setState({ activeTab: 'reflection' }, () => {
      const shouldStartReflection = !this.state.isReflectionPhase || this.state.reflectionIsComplete;
      if (shouldStartReflection) {
        this.triggerPomodoroReflection();
      }
    });
  };

  activateIssueTab = () => {
    this.setState({ activeTab: 'issue' });
  };

  triggerPomodoroReflection = async () => {
    const { chapter } = this.props;
    const chapterId = chapter?.id;
    // A reflection can be started any time there's a chapter. If no writer
    // session exists yet, create one so reflection answers are persisted.
    if (!chapterId) return;

    let reflectionWriterSessionId = this.props.writerSessionId || null;
    if (!reflectionWriterSessionId && typeof this.props.ensureWriterSessionForReflection === 'function') {
      reflectionWriterSessionId = await this.props.ensureWriterSessionForReflection();
    }

    // Reflection is attached to the same issue the user is fixing in this session
    // (shared with the ask chat), so questions can be personalized from history.
    const reflectionIssueId = this.getReflectionIssueId();

    // Start reflection phase
    this.setState({
      input: '',
      activeTab: 'reflection',
      isReflectionPhase: true,
      reflectionQuestionIndex: 0,
      reflectionIsComplete: false,
      reflectionRecordCreated: false,
      currentReflectionQuestion: null,
      reflectionWriterSessionId,
    });

    // Create a chat session for reflection
    try {
      const startReflectionUrl = this.state.enhancedReflection
        ? urlMap.startReflectionEnhanced()
        : urlMap.startReflection();
      const reflectionRes = await fetch(startReflectionUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          chapterId,
          issueId: reflectionIssueId,
          writerSessionId: reflectionWriterSessionId || undefined,
        }),
      });

      if (!reflectionRes.ok) {
        throw new Error(`Failed to start reflection session: HTTP ${reflectionRes.status}`);
      }

      const reflectionData = await reflectionRes.json();
      const activeSessionId = reflectionData?.sessionId;
      const firstQuestion = reflectionData?.question || reflectionData?.reply;

      if (!activeSessionId) {
        throw new Error('Failed to create reflection session');
      }

      if (!firstQuestion) {
        throw new Error('Missing initial reflection question from server');
      }

      this.setState({
        reflectionSessionId: activeSessionId,
        sending: false,
        typing: false,
        currentReflectionQuestion: firstQuestion,
        reflectionQuestionIndex: 0,
      });

      // Add initial prompt to messages
      const messageId = `reflection-init-${Date.now()}`;
      this.setState((s) => ({
        reflectionMessages: [
          ...s.reflectionMessages,
          {
            id: messageId,
            from: 'them',
            text: firstQuestion,
          },
        ],
      }));
    } catch (err) {
      this.setState({ sending: false, typing: false, error: err.message || 'Failed to start reflection' });
    }
  };

  applyReflectionResponseAndGetNext = async (responseText) => {
    const { reflectionSessionId, reflectionWriterSessionId, currentReflectionQuestion } = this.state;
    const { writerSessionId } = this.props;
    const activeWriterSessionId = reflectionWriterSessionId || writerSessionId || undefined;

    if (!reflectionSessionId) return;

    try {
      this.setState({ sending: true, typing: true });

      const reflectionMessagesUrl = this.state.enhancedReflection
        ? urlMap.reflectionMessagesEnhanced()
        : urlMap.reflectionMessages();
      const res = await fetch(reflectionMessagesUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          sessionId: reflectionSessionId,
          chapterId: this.props.chapter?.id,
          message: responseText,
          writerSessionId: activeWriterSessionId,
          issueId: this.getReflectionIssueId(),
          askedQuestion: currentReflectionQuestion || undefined,
        }),
      });

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const { reply, isComplete, questionIndex } = await res.json();

      // Append the next question (or completion message) to the chat.
      const messageId = `reflection-q-${Date.now()}`;
      this.setState((s) => ({
        sending: false,
        typing: false,
        isReflectionPhase: !isComplete,
        reflectionIsComplete: isComplete,
        currentReflectionQuestion: isComplete ? null : reply,
        reflectionQuestionIndex: isComplete ? s.reflectionQuestionIndex : questionIndex,
        reflectionMessages: [
          ...s.reflectionMessages,
          { id: messageId, from: 'them', text: reply },
        ],
      }));

      if (isComplete) {
        this.props.onReflectionComplete?.();
      }

      // Scroll to latest message.
      setTimeout(() => {
        const el = document.querySelector('.coach-messages');
        if (el) el.scrollTop = el.scrollHeight;
      }, 100);
    } catch (err) {
      console.warn('Error applying reflection response:', err);
      this.setState({ sending: false, typing: false });
    }
  };

  // ── render ──

  render() {
    const { onClose, projectId, chapter } = this.props;
    const {
      issueMessages, reflectionMessages, input, sending, typing,
      error, flowIssue, seedsUsed, isReflectionPhase, activeTab, enhancedReflection,
    } = this.state;
    const inProgress = this.getInProgressIssues();
    const reflectionTabActive = activeTab === 'reflection';
    const visibleMessages = reflectionTabActive ? reflectionMessages : issueMessages;

    // Show the flow panel when an issue is selected
    if (flowIssue) {
      return (
        <div className="coach-scroll">
          <aside className="coach-panel tasks-coach-panel" aria-label="Issue flow">
            <IssueFlowPanel
              issue={flowIssue}
              onClose={() => this.setState({ flowIssue: null })}
              onOpenInEditor={this.props.onOpenInEditor}
              onHelp={this.handleFlowHelp}
              helpMessages={issueMessages}
              helpTyping={typing}
              helpError={error}
              onIssueResolved={(issueId) => {
                this.setState((s) => ({
                  flowIssue: null,
                  projectIssues: s.projectIssues.map((i) =>
                    i.id === issueId ? { ...i, status: 'done' } : i
                  ),
                }));
              }}
            />
          </aside>
        </div>
      );
    }

    return (
      <div className="coach-scroll">
        <aside className="coach-panel tasks-coach-panel" aria-label="Chapter coach">
          {/* Header */}
          <div className="coach-header tasks-coach-header">
            <SeedMeter seedsUsed={seedsUsed} />
            <span className="tasks-coach-title" title="storyclub.ai coach">
              storyclub.ai coach
            </span>
            <span style={{ flex: 1 }} />
            <button
              className="btn subtle"
              onClick={this.onReset}
              title="Restart chat"
              aria-label="Restart chat"
            >
              <Icon.Restart />
            </button>
            <button
              type="button"
              className="btn subtle"
              onClick={onClose}
              aria-label="Close coach panel"
            >
              <Icon.Close />
            </button>
          </div>

          <div className="tasks-coach-tabs" role="tablist" aria-label="Issue solver sections">
            <button
              type="button"
              role="tab"
              aria-selected={!reflectionTabActive}
              className={`tasks-coach-tab ${!reflectionTabActive ? 'is-active' : ''}`}
              onClick={this.activateIssueTab}
            >
              Ask
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={reflectionTabActive}
              className={`tasks-coach-tab ${reflectionTabActive ? 'is-active' : ''}`}
              onClick={this.activateReflectionTab}
            >
              <Icon.Reflection />
              <span>Reflect</span>
              <span className="tasks-reflect-tip-anchor" aria-hidden="true">
                i
                <span className="tasks-reflect-tooltip" role="tooltip">
                  Reflection mode helps you close a sprint and plan your next move. Answer the prompts in the chat below.
                </span>
              </span>
            </button>
          </div>

          {reflectionTabActive && (
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                margin: '8px 10px 4px',
                fontSize: 12,
                color: 'var(--text-2)',
              }}
            >
              <input
                type="checkbox"
                checked={enhancedReflection}
                onChange={this.toggleEnhancedReflection}
                aria-label="Enhanced reflection"
              />
              <span>
                Enhanced reflection
                {' · '}
                <span style={{ color: 'var(--muted-2)' }}>uses generated questions, costs 1 seed per LLM question</span>
              </span>
            </label>
          )}

          {/* In-progress issues */}
          {!reflectionTabActive && (
          <div className="tasks-coach-context">
            {inProgress.length === 0 ? (
              <div className="tasks-coach-empty">
                <p>Pick something from the board.</p>
                {projectId && (
                  <p>
                    <a
                      href={`/project/${projectId}/library#issues`}
                      className="step-panel-link"
                    >
                      Open the message board
                    </a>{' '}
                    and pick something to work on.
                  </p>
                )}
              </div>
            ) : (
              <div className="tasks-coach-context-list">
                {inProgress.map((issue) => (
                  <div key={issue.id} style={{ display: 'grid', gridTemplateColumns: '24px 1fr', alignItems: 'start', gap: 8 }}>
                    <label
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: 10,
                        cursor: 'pointer',
                      }}
                      title="Include this issue as context"
                    >
                      <input
                        type="checkbox"
                        checked={this.state.selectedIssueId === issue.id}
                        onChange={() => this.toggleIssueSelection(issue.id)}
                        aria-label={`Include issue context: ${issue.title || 'Untitled issue'}`}
                      />
                    </label>
                    <IssueReadOnlyCard
                      issue={issue}
                      defaultExpanded={false}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Chat messages */}
          <div className="coach-messages">
            {error && <div className="coach-bubble them">⚠ {error}</div>}
            {visibleMessages.map((m) => (
              <div key={m.id} className={`coach-bubble ${m.from === 'me' ? 'me' : 'them'}`}>
                <ReactMarkdown className="coach-markdown" remarkPlugins={[remarkGfm]}>
                  {m.text}
                </ReactMarkdown>
              </div>
            ))}
            {typing && <TypingIndicator />}
          </div>

          {/* Chat input */}
          <div className="coach-input">
            <textarea
              ref={this.textareaRef}
              rows={2}
              placeholder={
                reflectionTabActive
                  ? 'Type your reflection…'
                  : sending
                  ? 'Thinking…'
                  : 'Type a message (general chat, no issue context)…'
              }
              value={input}
              disabled={
                sending ||
                (reflectionTabActive ? !isReflectionPhase : false)
              }
              onChange={this.onChange}
              onKeyDown={this.onKeyDown}
              className="coach-textarea"
            />
            <button
              type="button"
              className="btn primary"
              disabled={
                sending ||
                !input.trim() ||
                (reflectionTabActive ? !isReflectionPhase : false)
              }
              onClick={this.handleSend}
            >
              Send <span className="seed-price-label">1 fig seed</span>
            </button>
          </div>

        </aside>
      </div>
    );
  }
}
